import React, {useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";

import SectionHeader from "components/dashboard/SectionHeader";
import Toggle from "components/Toggle/Toggle";
import ToggleNavigation from "components/Toggle/ToggleNavigation";

import TopProductsTable from "./components/TopProductsTable";

import ShowMoreButton from "../TopTablesComponents/ShowMoreButton";
import TopTableHeaderContainer from "../TopTablesComponents/TopTableHeaderContainer";
import ExportButton from "../TopTablesComponents/ExportButton";
import TopTableHeaderActionContainer from "../TopTablesComponents/TopTableHeaderActionContainer";

import {greyishBlue100} from "assets/jss/main";

import {DateRangePickerContext} from "contexts/DateRangePickerContext";

import {getProductsFlops, getProductsRanking} from "api/dashboard/productsRanking";
import {exportTopProductsCsv} from "api/dashboard/csvExport";

import useScrollTrigger from "hooks/useScrollTrigger";
import {UserAgentUtils} from "../../../../../../utils/useragent";
import TopProductListMobile from "./components/TopProductListMobile";
import ShowMoreButtonMobile from "../TopTablesComponents/ShowMoreButtonMobile";

const isMobile = UserAgentUtils.isMobile();

function TopProducts({ retailer, entityName, organizationName }) {
    const { startDate, endDate, dateChangedTrigger } = useContext(DateRangePickerContext);

    const [activeTab, setActiveTab] = useState(0);
    const [showAll, setShowAll] = useState(false);
    const [ranking, setRanking] = useState(null);
    const [flops, setFlops] = useState(null);
    const [rankingLoading, setRankingLoading] = useState(false);
    const [flopsLoading, setFlopsLoading] = useState(false);

    const [sectionRef, triggered] = useScrollTrigger(dateChangedTrigger);

    useEffect(() => {
        if (!triggered || !startDate || !endDate) {
            return;
        }

        const start = startDate.format("Y-M-D");
        const end = endDate.format("Y-M-D");
        const retailers = retailer ? [retailer] : [];

        // a slower response for a previous date range must not overwrite the current one
        let cancelled = false;

        setRanking(null);
        setFlops(null);
        setShowAll(false);

        setRankingLoading(true);
        getProductsRanking(start, end, retailers, 10)
            .then(data => !cancelled && setRanking(data))
            .catch(() => !cancelled && setRanking(null))
            .finally(() => !cancelled && setRankingLoading(false));

        setFlopsLoading(true);
        getProductsFlops(start, end, retailers, 10)
            .then(data => !cancelled && setFlops(data))
            .catch(() => !cancelled && setFlops(null))
            .finally(() => !cancelled && setFlopsLoading(false));

        return () => {
            cancelled = true;
        };
    }, [triggered, retailer]);

    const isBestSellers = activeTab === 0;
    const rawData = isBestSellers ? ranking : flops;
    const loading = isBestSellers ? rankingLoading : flopsLoading;
    const totalItems = rawData?.lineItems?.length ?? 0;

    const displayData = rawData
        ? { ...rawData, lineItems: showAll ? rawData.lineItems ?? [] : (rawData.lineItems ?? []).slice(0, 5) }
        : null;

    return (
        <div ref={sectionRef}>
            <TopTableHeaderContainer>
                <SectionHeader
                    title={<FormattedMessage id="dashboard.topProducts.title" />}
                    subtitle={<FormattedMessage id="dashboard.topProducts.subtitle" />}
                />
                <TopTableHeaderActionContainer>
                    <ToggleNavigation
                        fullWidth={isMobile}
                        value={activeTab}
                        onChange={(value) => {setActiveTab(value); setShowAll(false);}}
                        autoMargin
                    >
                        <Toggle
                            fullWidth={isMobile}
                            label={<FormattedMessage id='dashboard.topProducts.tab.bestsellers' />}
                            autoHeight
                        />
                        <Toggle
                            fullWidth={isMobile}
                            label={<FormattedMessage id='dashboard.topProducts.tab.flops' />}
                            autoHeight
                        />
                    </ToggleNavigation>
                    <ExportButton
                        disabled={displayData === null || displayData.lineItems?.length === 0}
                        onClick={() => {
                            exportTopProductsCsv(
                                rawData?.lineItems ?? [],
                                startDate.format("Y-M-D"),
                                endDate.format("Y-M-D"),
                                entityName,
                                organizationName,
                                isBestSellers ? "top" : "flop"
                            );
                        }}
                    />
                </TopTableHeaderActionContainer>
            </TopTableHeaderContainer>

            {UserAgentUtils.isMobile() ? (
                <>
                    <TopProductListMobile data={displayData} loading={loading}  />
                    {!showAll && totalItems > 5 && (
                        <ShowMoreButtonMobile onClick={() => setShowAll(true)} />
                    )}
                </>

            ) : (
                <div
                    style={{
                        backgroundColor: "#fff",
                        borderRadius: "0px 0px 12px 12px",
                        border: `1px solid ${greyishBlue100}`,
                        borderTop: 'none',
                        margin: 0
                    }}
                >
                    <TopProductsTable data={displayData} loading={loading} />

                    {!showAll && totalItems > 5 && (
                        <ShowMoreButton onClick={() => setShowAll(true)} />
                    )}
                </div>
            )}

        </div>
    );
}

TopProducts.propTypes = {
    retailer: PropTypes.string,
    entityName: PropTypes.string,
    organizationName: PropTypes.string,
};

export default TopProducts;
